import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { of } from 'rxjs';
import { concatWith, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { Destroyer } from 'src/app/core/abstract/destroyer';
import { CRAFT_ITEM_SMALL_ICONS } from 'src/app/core/dictionaries/craft-items-icons';
import { CraftItem } from 'src/app/core/models/craft.models';
import { DBIdea } from 'src/app/core/models/idea.models';
import { CraftItemsBackendService } from 'src/app/core/services/backend/craft-items.backend.service';
import { IdeasLinkItemsStoreService } from 'src/app/core/store/services/ideas-store-link-items.service';

@Component({
  selector: 'craft-item-mapping',
  templateUrl: './craft-item-mapping.component.html',
  styleUrls: ['./craft-item-mapping.component.scss'],
  standalone: false,
})
export class CraftItemMappingComponent extends Destroyer implements OnInit {
  @Input() public idea: DBIdea;

  public isLoading = false;
  public items: CraftItem[];
  public openedItems = new Set<string>();
  public linkedItems = new Map<string, CraftItem>();

  public ICONS = CRAFT_ITEM_SMALL_ICONS;
  public searchControl = new UntypedFormControl();

  constructor(
    private cibs: CraftItemsBackendService, //
    private ideasLinkStore: IdeasLinkItemsStoreService,
  ) {
    super();
  }

  public ngOnInit() {
    of('')
      .pipe(
        concatWith(this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged())),
        switchMap((searchText) => {
          this.isLoading = !searchText;
          return this.cibs.getItemsTree(this.idea.productId, searchText, true);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((items) => {
        this.items = items;
        this.isLoading = false;

        if (this.searchControl.value) {
          this.openItems(this.items);
        } else {
          this.openedItems.clear();
        }
      });

    this.cibs
      .getLinkedItemsMap(this.idea.productId, this.idea.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((map) => {
        this.linkedItems = map;
      });
  }

  public identify(index: number, item?: CraftItem): string | number {
    return item?.guid || index;
  }

  public toggleParent(parent: CraftItem) {
    if (this.openedItems.has(parent.guid)) {
      this.openedItems.delete(parent.guid);
      parent.children.forEach((c) => this.openedItems.delete(c.guid));
    } else {
      this.openedItems.add(parent.guid);
      if (parent.children.length === 0) {
        this.loadChildren(parent);
      }
    }
  }

  public toggleLinkItem(item: CraftItem, event: MouseEvent) {
    event.stopPropagation();
    if (this.linkedItems.has(item.guid)) {
      this.unlinkItem(item);
    } else {
      this.linkItem(item);
    }
  }

  public linkItem(item: CraftItem) {
    this.linkedItems.set(item.guid, item);
    this.ideasLinkStore.linkItem(this.idea, item.id).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public unlinkItem(item: CraftItem) {
    this.linkedItems.delete(item.guid);
    this.ideasLinkStore.unlinkItem(this.idea, item.id).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private loadChildren(parent: CraftItem) {
    this.cibs
      .getItemsList(this.idea.productId, parent.guid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((children) => {
        parent.children = children;
      });
  }

  private openItems(items: readonly CraftItem[]) {
    items.forEach((i) => {
      this.openedItems.add(i.guid);
      if (i.children.length > 0) {
        this.openItems(i.children);
      }
    });
  }
}
